import { DateTime } from 'luxon';
import {
  EXTENDED_FORECAST_CTA_DAY,
  isExtendedForecastCtaDay,
} from '../forecastHorizon';

export const WEATHER_FORECAST_DAY_QUERY_PARAM = 'day';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isValidISODate = (dateOnly: string): boolean =>
  DateTime.fromISO(dateOnly).isValid;

export const normalizeWeatherForecastDayDate = (
  value: string | null | undefined,
): string | undefined => {
  if (value == null || value === '') {
    return undefined;
  }

  if (DATE_ONLY_PATTERN.test(value)) {
    return isValidISODate(value) ? value : undefined;
  }

  const datePrefix = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (datePrefix) {
    return isValidISODate(datePrefix[1]) ? datePrefix[1] : undefined;
  }

  const parsed = DateTime.fromISO(value, { setZone: true });
  if (!parsed.isValid) {
    return undefined;
  }

  return parsed.toISODate() ?? undefined;
};

export const getWeatherForecastDayFromSearch = (
  search: string,
): string | undefined => {
  const day = new URLSearchParams(search).get(WEATHER_FORECAST_DAY_QUERY_PARAM);

  if (isExtendedForecastCtaDay(day)) {
    return EXTENDED_FORECAST_CTA_DAY;
  }

  return normalizeWeatherForecastDayDate(day);
};

export const buildWeatherForecastDaySearch = (
  selectedDate?: string,
  defaultDate?: string,
): string => {
  if (isExtendedForecastCtaDay(selectedDate)) {
    const params = new URLSearchParams();
    params.set(WEATHER_FORECAST_DAY_QUERY_PARAM, EXTENDED_FORECAST_CTA_DAY);
    return `?${params.toString()}`;
  }

  const normalizedSelectedDate = normalizeWeatherForecastDayDate(selectedDate);
  if (normalizedSelectedDate == null) {
    return '';
  }

  const normalizedDefaultDate = normalizeWeatherForecastDayDate(defaultDate);
  if (
    normalizedDefaultDate != null &&
    normalizedSelectedDate === normalizedDefaultDate
  ) {
    return '';
  }

  const params = new URLSearchParams();
  params.set(WEATHER_FORECAST_DAY_QUERY_PARAM, normalizedSelectedDate);
  return `?${params.toString()}`;
};

export const resolveWeatherForecastSelectedDayIndex = (
  forecastDays: { date: string }[],
  selectedDateFromSearch?: string,
  options?: { ctaPageEnabled?: boolean },
): number => {
  if (!forecastDays.length) {
    return 0;
  }

  if (
    options?.ctaPageEnabled &&
    isExtendedForecastCtaDay(selectedDateFromSearch)
  ) {
    return forecastDays.length;
  }

  const normalizedSelectedDate = normalizeWeatherForecastDayDate(
    selectedDateFromSearch,
  );
  if (normalizedSelectedDate == null) {
    return 0;
  }

  const dayIndex = forecastDays.findIndex(
    (forecastDay) =>
      normalizeWeatherForecastDayDate(forecastDay.date) ===
      normalizedSelectedDate,
  );

  return dayIndex >= 0 ? dayIndex : 0;
};
